/**
 * useGroupSpaceManagement Hook
 * 그룹 공간 관리 상태 관리 및 비즈니스 로직
 */
import { useState, useCallback, useEffect } from 'react';
import type {
  GroupSpace,
  Reservation,
  TabType,
  ModalState,
  GroupSpaceStats,
} from '../models/GroupSpaceManagement';
import { groupSpaceManagementRepository } from '../repositories/GroupSpaceManagementRepository';
import { groupSpaceManagementService } from '../services/GroupSpaceManagementService';

/**
 * useGroupSpaceManagement 커스텀 훅
 */
export const useGroupSpaceManagement = () => {
  // 상태 관리
  const [activeTab, setActiveTab] = useState<TabType>('spaces');
  const [spaces, setSpaces] = useState<GroupSpace[]>([]);
  const [reservations, setReservations] = useState<Reservation[]>([]);
  const [selectedSpace, setSelectedSpace] = useState<GroupSpace | null>(null);
  const [selectedReservation, setSelectedReservation] = useState<Reservation | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // 모달 상태
  const [modals, setModals] = useState<ModalState>({
    space: false,
    reservation: false,
  });

  /**
   * 통계 계산
   */
  const stats: GroupSpaceStats = groupSpaceManagementService.calculateStats(
    spaces,
    reservations
  );

  /**
   * 그룹 공간 목록 로드
   */
  const loadSpaces = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await groupSpaceManagementRepository.getGroupSpaces();
      setSpaces(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err instanceof Error ? err.message : '공간 목록 로드 실패');
      console.error('공간 목록 로드 실패:', err);
      setSpaces([]); // 에러 시 빈 배열로 설정
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * 예약 목록 로드
   */
  const loadReservations = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await groupSpaceManagementRepository.getReservations();
      setReservations(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err instanceof Error ? err.message : '예약 목록 로드 실패');
      console.error('예약 목록 로드 실패:', err);
      setReservations([]); // 에러 시 빈 배열로 설정
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * 모든 데이터 로드
   */
  const loadData = useCallback(async () => {
    await Promise.all([loadSpaces(), loadReservations()]);
  }, [loadSpaces, loadReservations]);

  /**
   * 초기 데이터 로드
   */
  useEffect(() => {
    loadData();
  }, [loadData]);

  /**
   * 탭 변경 핸들러
   */
  const handleTabChange = useCallback((tab: TabType) => {
    setActiveTab(tab);
  }, []);

  /**
   * 모달 열기
   */
  const openModal = useCallback((modalType: keyof ModalState) => {
    setModals((prev) => ({ ...prev, [modalType]: true }));
  }, []);

  /**
   * 모달 닫기
   */
  const closeModal = useCallback((modalType: keyof ModalState) => {
    setModals((prev) => ({ ...prev, [modalType]: false }));
    if (modalType === 'space') {
      setSelectedSpace(null);
    } else if (modalType === 'reservation') {
      setSelectedReservation(null);
    }
  }, []);

  /**
   * 공간 생성 모달 열기
   */
  const handleCreateSpace = useCallback(() => {
    setSelectedSpace(null);
    openModal('space');
  }, [openModal]);

  /**
   * 공간 수정 모달 열기
   */
  const handleEditSpace = useCallback(
    (space: GroupSpace) => {
      setSelectedSpace(space);
      openModal('space');
    },
    [openModal]
  );

  /**
   * 공간 삭제
   */
  const handleDeleteSpace = useCallback(
    async (spaceId: string) => {
      if (!window.confirm('정말 이 공간을 삭제하시겠습니까?')) {
        return;
      }

      try {
        setLoading(true);
        await groupSpaceManagementRepository.deleteGroupSpace(spaceId);
        await loadSpaces();
        alert('공간이 삭제되었습니다.');
      } catch (err) {
        alert(err instanceof Error ? err.message : '공간 삭제 실패');
        console.error('공간 삭제 실패:', err);
      } finally {
        setLoading(false);
      }
    },
    [loadSpaces]
  );

  /**
   * 공간 저장 (생성/수정)
   */
  const handleSaveSpace = useCallback(
    async (spaceData: Partial<GroupSpace>) => {
      try {
        setLoading(true);
        if (selectedSpace) {
          await groupSpaceManagementRepository.updateGroupSpace(
            selectedSpace.id,
            spaceData
          );
          alert('공간이 수정되었습니다.');
        } else {
          await groupSpaceManagementRepository.createGroupSpace(spaceData);
          alert('공간이 생성되었습니다.');
        }
        await loadSpaces();
        closeModal('space');
      } catch (err) {
        alert(err instanceof Error ? err.message : '공간 저장 실패');
        console.error('공간 저장 실패:', err);
      } finally {
        setLoading(false);
      }
    },
    [selectedSpace, loadSpaces, closeModal]
  );

  /**
   * 예약 생성 모달 열기
   */
  const handleCreateReservation = useCallback(
    (space?: GroupSpace) => {
      if (space) {
        setSelectedSpace(space);
      }
      setSelectedReservation(null);
      openModal('reservation');
    },
    [openModal]
  );

  /**
   * 예약 수정 모달 열기
   */
  const handleEditReservation = useCallback(
    (reservation: Reservation) => {
      setSelectedReservation(reservation);
      openModal('reservation');
    },
    [openModal]
  );

  /**
   * 예약 취소
   */
  const handleCancelReservation = useCallback(
    async (reservationId: string) => {
      if (!window.confirm('정말 이 예약을 취소하시겠습니까?')) {
        return;
      }

      try {
        setLoading(true);
        await groupSpaceManagementRepository.cancelReservation(reservationId);
        await loadReservations();
        alert('예약이 취소되었습니다.');
      } catch (err) {
        alert(err instanceof Error ? err.message : '예약 취소 실패');
        console.error('예약 취소 실패:', err);
      } finally {
        setLoading(false);
      }
    },
    [loadReservations]
  );

  /**
   * 예약 저장 (생성/수정)
   */
  const handleSaveReservation = useCallback(
    async (reservationData: Partial<Reservation>) => {
      try {
        setLoading(true);
        if (selectedReservation) {
          await groupSpaceManagementRepository.updateReservation(
            selectedReservation.id,
            reservationData
          );
          alert('예약이 수정되었습니다.');
        } else {
          await groupSpaceManagementRepository.createReservation(reservationData);
          alert('예약이 생성되었습니다.');
        }
        await loadReservations();
        closeModal('reservation');
      } catch (err) {
        alert(err instanceof Error ? err.message : '예약 저장 실패');
        console.error('예약 저장 실패:', err);
      } finally {
        setLoading(false);
      }
    },
    [selectedReservation, loadReservations, closeModal]
  );

  return {
    // 상태
    activeTab,
    spaces,
    reservations,
    selectedSpace,
    selectedReservation,
    loading,
    error,
    modals,
    stats,

    // 탭 관리
    handleTabChange,

    // 모달 관리
    openModal,
    closeModal,

    // 공간 관리
    handleCreateSpace,
    handleEditSpace,
    handleDeleteSpace,
    handleSaveSpace,

    // 예약 관리
    handleCreateReservation,
    handleEditReservation,
    handleCancelReservation,
    handleSaveReservation,

    // 데이터 로드
    loadData,
    loadSpaces,
    loadReservations,
  };
};
